/**
 * Minimal Pan.do/ra API client (docs/pandora-api.md).
 * Single endpoint: POST {site}/api/ with form-encoded `action` + `data` (JSON).
 * CORS is open on /api/ for all supported instances, so we call sites directly.
 */

export interface PandoraCondition {
  key: string;
  operator: string; // '=' contains, '==' is, '^' starts, '$' ends, '<', '>', …
  value: string | number | boolean;
}

export interface PandoraQuery {
  conditions: (PandoraCondition | PandoraQuery)[];
  operator: '&' | '|';
}

export interface PandoraItem {
  id: string;
  title: string;
  director: string[] | null;
  year: string | number | null;
  duration: number | null;
  parts: number;
  rightslevel: number;
}

export interface PandoraItemDetail extends PandoraItem {
  durations: number[]; // per-part seconds
  posterFrame: number | null;
  source?: string | null;
}

export interface PandoraAnnotation {
  id: string; // "ITEM/AB" — item id is the prefix before '/'
  in: number;
  out: number;
  value: string;
  title?: string;
  layer?: string; // annotation layer id (transcripts, keywords, …)
}

/** All annotations of an item grouped by layer id (from get {keys:["layers"]}). */
export type PandoraLayers = Record<string, PandoraAnnotation[]>;

/** Item text metadata: description (summary) + keywords. */
export interface PandoraItemText {
  summary: string; // description, HTML-ish
  keywords: string[];
}

/** One entry of `site.itemKeys` from `init` — queryable/sortable item metadata keys. */
export interface PandoraItemKey {
  id: string;
  title?: string;
  type: string | string[];
  sortable?: boolean;
}

/** One entry of `site.layers` from `init` — per-instance annotation layer definition. */
export interface PandoraLayer {
  id: string;
  title: string;
  type?: string; // 'text' | 'string' | 'place' | 'event'
  isSubtitles?: boolean;
}

/** Per-instance site configuration, the useful subset of `init` → `data.site`. */
export interface PandoraSiteInfo {
  itemKeys: PandoraItemKey[];
  layers: PandoraLayer[];
  totals?: { items?: number };
}

interface PandoraResponse<T> {
  status: { code: number; text: string };
  data: T;
}

/**
 * One archive request, as it went over the wire.
 *
 * Exists so the AI assistant's steps can be examined: the whole point is to show
 * the *exact* query, so this is emitted from inside `pandoraApi` rather than
 * reconstructed by a caller. A reconstruction would drift silently the moment
 * a default `keys`/`sort` changed here, and a debugging tool that lies about
 * the request is worse than none.
 *
 * `responseText` is the raw body, uncapped — it is only ever held in the
 * per-session memory cache (state/agentTrace.ts), never written to the CRDT.
 */
export interface PandoraWireCall {
  site: string;
  action: string;
  /** The `data` payload as an object, before JSON.stringify. */
  data: unknown;
  ms: number;
  ok: boolean;
  /** Served from `siteInfoCache` — no request was made. */
  cached?: boolean;
  httpStatus?: number;
  /** `status.code` from the pan.do/ra envelope, which is not the HTTP status. */
  apiStatus?: number;
  apiText?: string;
  /** Byte length of the raw body — not `.length`, which counts UTF-16 units. */
  bytes?: number;
  responseText?: string;
  error?: string;
}

export type PandoraObserver = (call: PandoraWireCall) => void;

/**
 * `observe` is threaded through only the wrappers `agent/tools/port.ts` calls.
 * Keep it that way: a module-level "current observer" would capture the whole
 * app's archive traffic (SearchPanel, BinPanel, ItemDetail, partoffsets all hit
 * these same functions) and misattribute it to whatever tool was in flight.
 */
async function pandoraApi<T>(
  site: string,
  action: string,
  data: unknown,
  observe?: PandoraObserver,
): Promise<T> {
  const body = new URLSearchParams();
  body.set('action', action);
  body.set('data', JSON.stringify(data));
  const started = Date.now();

  // An observer is a debugging aid and must never be able to break a request.
  const emit = (call: Omit<PandoraWireCall, 'site' | 'action' | 'data' | 'ms'>) => {
    if (!observe) return;
    try {
      observe({ site, action, data, ms: Date.now() - started, ...call });
    } catch {
      /* ignore */
    }
  };

  let res: Response;
  try {
    res = await fetch(`${site}/api/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch (err) {
    const error = err instanceof Error ? err.message : String(err);
    emit({ ok: false, error });
    throw err;
  }

  // Read the body BEFORE the status check, so a failing request still records
  // what the server actually said.
  const raw = await res.text();
  const bytes = new TextEncoder().encode(raw).length;

  if (!res.ok) {
    emit({ ok: false, httpStatus: res.status, bytes, responseText: raw });
    throw new Error(`pandora ${action}: HTTP ${res.status}`);
  }

  let json: PandoraResponse<T>;
  try {
    json = JSON.parse(raw) as PandoraResponse<T>;
  } catch {
    emit({
      ok: false,
      httpStatus: res.status,
      bytes,
      responseText: raw,
      error: 'response was not JSON',
    });
    throw new Error(`pandora ${action}: response was not JSON`);
  }

  if (json.status.code !== 200) {
    emit({
      ok: false,
      httpStatus: res.status,
      apiStatus: json.status.code,
      apiText: json.status.text,
      bytes,
      responseText: raw,
    });
    throw new Error(`pandora ${action}: ${json.status.code} ${json.status.text}`);
  }

  emit({
    ok: true,
    httpStatus: res.status,
    apiStatus: json.status.code,
    apiText: json.status.text,
    bytes,
    responseText: raw,
  });
  return json.data;
}

const siteInfoCache = new Map<string, Promise<PandoraSiteInfo>>();

/**
 * Site configuration via `init {}` → `data.site` (itemKeys, layer definitions,
 * item totals when the instance reports them). Cached per site as a promise
 * (same pattern as engine/partoffsets.ts); a rejected fetch is evicted so the
 * next call can retry.
 */
export async function getSiteInfo(
  site: string,
  observe?: PandoraObserver,
): Promise<PandoraSiteInfo> {
  const hit = siteInfoCache.get(site);
  if (hit) {
    // The cache is module-level and shared with the rest of the app, so the
    // assistant usually never hits the wire for `init`. Say so, rather than
    // leaving a hole in the trace that reads as a missing request.
    if (observe) {
      try {
        observe({ site, action: 'init', data: {}, ms: 0, ok: true, cached: true });
      } catch {
        /* ignore */
      }
    }
    return hit;
  }
  const fresh = pandoraApi<{
    site?: {
      itemKeys?: PandoraItemKey[];
      layers?: PandoraLayer[];
      totals?: { items?: number };
    };
  }>(site, 'init', {}, observe).then((data) => {
    const config = data.site ?? {};
    const info: PandoraSiteInfo = {
      itemKeys: config.itemKeys ?? [],
      layers: config.layers ?? [],
    };
    if (config.totals?.items != null) info.totals = { items: config.totals.items };
    return info;
  });
  fresh.catch(() => {
    if (siteInfoCache.get(site) === fresh) siteInfoCache.delete(site);
  });
  siteInfoCache.set(site, fresh);
  return fresh;
}

/** Test hook. */
export function __clearSiteInfoCache(): void {
  siteInfoCache.clear();
}

const ITEM_KEYS = ['id', 'title', 'director', 'year', 'duration', 'parts', 'rightslevel'];

export interface FindItemsOptions {
  query: string;
  range: [number, number];
  /** adds {canplayvideo == true} — useful on indiancine.ma / 0xdb.org */
  streamableOnly?: boolean;
}

export async function findItems(
  site: string,
  opts: FindItemsOptions,
): Promise<PandoraItem[]> {
  const conditions: PandoraCondition[] = [];
  if (opts.query.trim())
    conditions.push({ key: '*', operator: '=', value: opts.query.trim() });
  if (opts.streamableOnly)
    conditions.push({ key: 'canplayvideo', operator: '==', value: true });
  const data = await pandoraApi<{ items: PandoraItem[] }>(site, 'find', {
    query: { conditions, operator: '&' },
    keys: ITEM_KEYS,
    range: opts.range,
    sort: [{ key: 'modified', operator: '-' }],
  });
  return data.items ?? [];
}

export interface PandoraSort {
  key: string;
  operator: '+' | '-';
}

export interface FindItemsAdvancedOptions {
  query: PandoraQuery;
  keys?: string[];
  range: [number, number];
  sort?: PandoraSort[];
}

/** Generalized `find` for the advanced search overlay: full query + sort control. */
export async function findItemsAdvanced(
  site: string,
  opts: FindItemsAdvancedOptions,
  observe?: PandoraObserver,
): Promise<PandoraItem[]> {
  const data = await pandoraApi<{ items: PandoraItem[] }>(
    site,
    'find',
    {
      query: opts.query,
      keys: opts.keys ?? ITEM_KEYS,
      range: opts.range,
      sort: opts.sort ?? [{ key: 'modified', operator: '-' }],
    },
    observe,
  );
  return data.items ?? [];
}

/** Total match count: `find` without `keys` → `{items: <int>}` (§2 response shapes). */
export async function findItemsCount(site: string, query: PandoraQuery): Promise<number> {
  const data = await pandoraApi<{ items: number }>(site, 'find', { query });
  return data.items ?? 0;
}

/** One facet bucket from a grouped `find`. */
export interface PandoraFacet {
  name: string;
  items: number; // matching item count
}

/**
 * Facet counts: `find` with `group: "<itemKey>"` → `{items: [{name, items}, ...]}`
 * (the browser-filter shape, §2), sorted by count descending.
 */
export async function findFacets(
  site: string,
  query: PandoraQuery,
  group: string,
  range: [number, number] = [0, 20],
): Promise<PandoraFacet[]> {
  const data = await pandoraApi<{ items: PandoraFacet[] }>(site, 'find', {
    query,
    group,
    range,
    sort: [{ key: 'items', operator: '-' }],
  });
  return data.items ?? [];
}

export async function getItem(
  site: string,
  id: string,
  observe?: PandoraObserver,
): Promise<PandoraItemDetail> {
  return pandoraApi<PandoraItemDetail>(
    site,
    'get',
    {
      id,
      keys: [
        'id',
        'title',
        'director',
        'year',
        'duration',
        'durations',
        'parts',
        'posterFrame',
        'rightslevel',
        'source',
      ],
    },
    observe,
  );
}

/** One clip of an Edit — a source item with an in/out range in concatenated item time. */
export interface PandoraEditClip {
  item: string; // source item id
  in: number; // seconds, concatenated item time
  out: number;
  title?: string;
  parts?: number;
  durations?: number[]; // per-part seconds of the source item
}

/** A pan.do/ra Edit: an ordered sequence of clips. `id` is "<user>:<name>". */
export interface PandoraEdit {
  id: string;
  name?: string;
  user?: string;
  clips: PandoraEditClip[];
}

/**
 * Fetch an Edit and its resolved clip sequence via `getEdit {id, keys}`. Each
 * clip already carries `item`, `in`/`out`, `title`, `parts`, and `durations`,
 * so importing an Edit needs no per-clip `get` call.
 */
export async function getEdit(site: string, id: string): Promise<PandoraEdit> {
  const data = await pandoraApi<PandoraEdit>(site, 'getEdit', {
    id,
    keys: ['id', 'name', 'user', 'clips'],
  });
  return { ...data, clips: data.clips ?? [] };
}

export async function findAnnotations(
  site: string,
  query: string,
  range: [number, number],
  layer?: string,
  observe?: PandoraObserver,
): Promise<PandoraAnnotation[]> {
  const conditions: PandoraCondition[] = [];
  if (query.trim()) conditions.push({ key: 'value', operator: '=', value: query.trim() });
  if (layer) conditions.push({ key: 'layer', operator: '==', value: layer });
  const data = await pandoraApi<{ items: PandoraAnnotation[] }>(
    site,
    'findAnnotations',
    {
      query: { conditions, operator: '&' },
      keys: ['id', 'in', 'out', 'value', 'title', 'layer'],
      range,
      sort: [{ key: 'id', operator: '+' }],
    },
    observe,
  );
  return data.items ?? [];
}

/** Keys worth asking for on a moment search: enough to build a clip card. */
const ANNOTATION_KEYS = ['id', 'in', 'out', 'value', 'title', 'layer'];

/**
 * Ordering that makes a moment list readable: grouped by film, chronological
 * within it. The default `id` sort is lexical over `ITEM/ANNID`, which is
 * effectively arbitrary within an item and hides the fact that two hits are
 * seconds apart in the same scene.
 */
const ANNOTATION_SORT: PandoraSort[] = [
  { key: 'item', operator: '+' },
  { key: 'in', operator: '+' },
];

export interface FindAnnotationsAdvancedOptions {
  /** Prebuilt condition tree over annotation keys (value, layer, item, …). */
  query: PandoraQuery;
  /**
   * A nested **item** query constraining which items' annotations are searched
   * (pandora-api-reference §2.1). This is how a moment search gets access to
   * item metadata — year, director, rightslevel — which the annotation query
   * itself cannot see.
   */
  itemsQuery?: PandoraQuery;
  keys?: string[];
  range: [number, number];
  sort?: PandoraSort[];
}

/**
 * Generalized `findAnnotations`: full condition tree, item-metadata constraint
 * and sort control.
 *
 * There is deliberately no count sibling to `findItemsCount`. `findAnnotations`
 * with no `keys` returns HTTP 500 on the live instances (verified against
 * indiancine.ma), and counting by asking for ids alone is not cheap here: a
 * broad term over the shot-description corpus is 48k rows and ~900KB. Callers
 * that want to know whether there is more should over-fetch by one row. The sibling of `findItemsAdvanced`, and for the same
 * reason — the plain `findAnnotations` below covers the "one search box" case
 * the panels need, and widening it would push its two callers into building
 * query trees they do not want to know about.
 */
export async function findAnnotationsAdvanced(
  site: string,
  opts: FindAnnotationsAdvancedOptions,
  observe?: PandoraObserver,
): Promise<PandoraAnnotation[]> {
  const data = await pandoraApi<{ items: PandoraAnnotation[] }>(
    site,
    'findAnnotations',
    {
      query: opts.query,
      ...(opts.itemsQuery ? { itemsQuery: opts.itemsQuery } : {}),
      keys: opts.keys ?? ANNOTATION_KEYS,
      range: opts.range,
      sort: opts.sort ?? ANNOTATION_SORT,
    },
    observe,
  );
  return data.items ?? [];
}

/**
 * All annotations of an item grouped by layer, in one call:
 * `get {id, keys:["layers"]}` → `{layers: {layerId: annotation[]}}`.
 */
export async function getLayers(
  site: string,
  id: string,
  observe?: PandoraObserver,
): Promise<PandoraLayers> {
  const data = await pandoraApi<{ layers?: PandoraLayers }>(
    site,
    'get',
    { id, keys: ['layers'] },
    observe,
  );
  return data.layers ?? {};
}

/**
 * Item description + keywords. `summary` is the item description (HTML-ish);
 * `keywords` may arrive as a string[] or a comma-joined string depending on the
 * instance — normalized to a flat string[] here.
 */
export async function getItemText(site: string, id: string): Promise<PandoraItemText> {
  const data = await pandoraApi<{ summary?: string; keywords?: unknown }>(site, 'get', {
    id,
    keys: ['summary', 'keywords'],
  });
  return { summary: data.summary ?? '', keywords: normalizeKeywords(data.keywords) };
}

/** Coerce an item's `keywords` value (string[] | string | undefined) to string[]. */
export function normalizeKeywords(raw: unknown): string[] {
  const out: string[] = [];
  const push = (s: string) => {
    for (const part of s.split(',')) {
      const k = stripHtml(part).trim();
      if (k) out.push(k);
    }
  };
  if (typeof raw === 'string') push(raw);
  else if (Array.isArray(raw))
    for (const v of raw) {
      if (typeof v === 'string') push(v);
      else if (v && typeof v === 'object' && 'value' in v)
        push(String((v as { value: unknown }).value));
    }
  // dedupe, preserve order
  return Array.from(new Set(out));
}

export async function autocomplete(
  site: string,
  key: string,
  value: string,
  range: [number, number] = [0, 10],
): Promise<string[]> {
  const data = await pandoraApi<{ items: string[] }>(site, 'autocomplete', {
    key,
    value,
    operator: '=',
    range,
  });
  return data.items ?? [];
}

/** Item id from an annotation id ("KDK/AB" → "KDK"). */
export function annotationItemId(annotationId: string): string {
  return annotationId.split('/')[0];
}

/** Annotation values are HTML-ish (<br/> etc.) — strip tags for snippets. */
export function stripHtml(value: string): string {
  if (typeof value !== 'string') value = value == null ? '' : String(value);
  return value
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}
